import {useResourceTypes} from "../../../Apis/Admin/ResourceTypeAdminApi";
import FlexRow from "../../../Components/FlexRow";
import {StyledText} from "../../../Components/Text";
import Table from "../../../Components/Table";
import Box from "../../../Components/Box";
import {useTranslation} from "react-i18next";
import Button from "../../../Components/Button";
import React, {useEffect, useState} from "react";
import Gap from 'Components/Gap';
import {useAgreements} from "../../../Apis/Admin/AgreementAdminApi";
import {
    useDeleteResource,
    useDeleteResourceBulk,
    useResources,
    useSaveResource
} from "../../../Apis/Admin/ResourcesAdminApi";
import {ResourceDto} from "../../../Apis/Models/ResourceDto";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import Modal from "../../../Components/Modal";
import Space from "../../../Components/Space";
import InputLabel from "../../../Components/InputLabel";
import FileUploadButton, {FileUploadButtonV2} from "../../../Components/FileUploadButton";
import DropdownItem from "../../../Components/DropdownItem";
import Dropdown from "../../../Components/Dropdown";
import CheckmarkCircle from "../../../Assets/Icons/CheckmarkCircle";
import AgreementSelector from "../../../Features/Dashboard/Components/AgreementSelector";
import The0MissionIcon from "../../../Assets/Icons/The0MissionIcon";
import useSelectedAgreement from "../../../Features/Dashboard/Hooks/useSelectedAgreement";
import useToast from "Hooks/useToast";
import {useNavigate} from "react-router-dom";
import {ControlledInput} from "../../../Components/Input";
import Separator from "../../../Components/Separator";
import CloseIcon from "../../../Assets/Icons/CloseIcon";
import ResourceBulkUploadModal from "../../../Features/Resources/ResourceBulkUploadModal";
import {useSelectEntries} from "../../../Hooks/UseSelectEntries";

const ResourcesPage = () => {

    const {t} = useTranslation();
    const navigate = useNavigate();

    const selectedAgreement = useSelectedAgreement();

    const {data: resourceTypes, isLoading: isLoadingResourceTypes} = useResourceTypes();
    const {data: agreements, isLoading: isLoadingAgreements} = useAgreements()
    const {data: resources, isLoading: isLoadingResources} = useResources(selectedAgreement);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, selectedItems, isSelected, toggleSelected, toggleAllSelected] = useSelectEntries(resources?.map(x => x.id) ?? [])

    const [bulkModalVisible, setBulModalVisible] = useState<boolean>(false);
    const [mutateModalVisible, setMutateModalVisible] = useState<boolean>(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

    const { showSuccessToast, showErrorToast} = useToast();

    const {handleSubmit, register, control, reset, watch, setValue} = useForm<ResourceDto>();
    const { fields, append, remove } = useFieldArray({ control, name: "resourceFiles"})

    const {mutateAsync: saveResourceMutation, isLoading: isSavingResource} = useSaveResource();
    const {mutateAsync: deleteResourceMutation, isLoading: isDeletingResource} = useDeleteResource();
    const {mutateAsync: deleteResourcesBulkMutation, isLoading: isDeletingResources} = useDeleteResourceBulk();

    useEffect(() => {
        setValue('agreementIds', selectedAgreement ? [selectedAgreement.id] : [])
    }, [selectedAgreement, setValue])

    const closeModal = () => {
        setMutateModalVisible(false);
        setDeleteModalVisible(false);
        reset({});
    }

    const selectResource = (resource: ResourceDto) => {
        reset(resource);
        setMutateModalVisible(true);
    }

    const saveResource = async (data: ResourceDto) => {
        const resourceType = resourceTypes?.find(x => x.id === data.resourceTypeId)!;

        if (!data.link && !resourceType.link && data.resourceFiles.length === 0 && !data.preview) {
            showErrorToast(t('resourceFileRequired'));
            return;
        }

        await saveResourceMutation(data);

        closeModal();
    }

    const deleteResource = async (resource: ResourceDto) => {
        reset(resource);
        setDeleteModalVisible(true)
    }

    const confirmDeleteResource = async (data: ResourceDto) => {
        await deleteResourceMutation(data);

        closeModal();
    }

    const bulkDelete = async () => {
        await deleteResourcesBulkMutation(selectedItems)

        toggleAllSelected(false)

        showSuccessToast(t('resourcesDeleted'));
    }

    return (
        <Box>
            <Gap size={"medium"} block direction={"vertical"}>
                <FlexRow justify={"space-between"}>
                    <Gap size={"medium"}>
                        <StyledText type={"Lead"} weight={"Bold"} color={"secondary"}>{t('resources.title')}</StyledText>
                        <Button text={t('showResourceTypes')} color={"link"} onClick={() => navigate('/admin/resourceTypes')}/>
                    </Gap>

                    <Gap size={"medium"}>
                        {selectedItems.length > 0 && (
                            <Button state={isDeletingResources ? "Loading" : "None"} color={"link"} onClick={bulkDelete} text={`- ${t('bulkDelete')}`}/>
                        )}
                        <Button color={"link"} onClick={() => setMutateModalVisible(true)} text={`+ ${t('createResource')}`}/>
                        <Button color={"link"} onClick={() => setBulModalVisible(true)} text={`+ ${t('bulkCreate')}`}/>
                    </Gap>
                </FlexRow>
                <FlexRow justify={"space-between"}>
                    <AgreementSelector position={"Under Left"}/>
                </FlexRow>
            </Gap>

            {(isLoadingResourceTypes || isLoadingAgreements || isLoadingResources) ? (
                <FlexRow align={"center"} justify={"center"}><The0MissionIcon spin width={50} height={50}></The0MissionIcon></FlexRow>
            ) : (
                <>
                    <Table>
                        <thead>
                        <tr>
                            <th>
                            </th>
                            <th>
                                {t('title')}
                            </th>
                            <th>
                                {t('description')}
                            </th>
                            <th>
                                {t('fileName')}
                            </th>
                            <th>
                                {t('preview')}
                            </th>
                            <th>
                                {t('link')}
                            </th>
                            <th>

                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {resources?.map((resource, index) => {
                            let resourceType = resourceTypes?.find(x => x.id === resource.resourceTypeId)!;

                            return (
                                <tr key={resource.id}>
                                    <td>
                                        <input type={"checkbox"} onClick={() => toggleSelected(resource.id!)} checked={isSelected(resource.id)} />
                                    </td>
                                    <td>
                                        {!!resourceType.icon && (
                                            <img src={resourceType.icon.url} alt={t('icon')} width={80}
                                                 height={"auto"}/>
                                        )}
                                    </td>
                                    <td>
                                        {resourceType.title}
                                    </td>
                                    <td>
                                        {resourceType.description}
                                    </td>
                                    <td title={resource.resourceFiles?.map(x => x.name).join(', ')}>
                                        {resource.resourceFiles?.map(x => x.name).join(', ')}
                                    </td>
                                    <td>
                                        {resource.preview && (
                                            <img src={resource.preview.url} alt={t('preview')} width={80}
                                                 height={"auto"}/>
                                        )}
                                        {resourceType.preview && (
                                            <img src={resourceType.preview.url} alt={t('preview')} width={80}
                                                 height={"auto"}/>
                                        )}
                                    </td>
                                    <td>
                                        <a href={resource.link} target={"_blank"} rel="noreferrer" >{resource.link}</a>
                                    </td>
                                    <td>
                                        <Gap size={"medium"}>
                                    <span className={"clickable"} onClick={() => selectResource(resource)}>
                                        {t('update')}
                                    </span>
                                            <span className={"clickable"} onClick={() => deleteResource(resource)}>
                                        {t('delete')}
                                    </span>
                                        </Gap>
                                    </td>
                                </tr>
                            )
                        })}
                        </tbody>
                    </Table>

                </>
            )}
            
            <Modal visible={mutateModalVisible} onCancel={closeModal} title={t('createResource')}>
                <form onSubmit={handleSubmit(saveResource)}>
                    <input type={"hidden"} {...register('id')} />
                    <Space direction={"column"} size={"large"}>
                        <div className={"w-100"}>
                            <InputLabel>
                                {t('title')}
                            </InputLabel>
                            <Controller control={control} name={"resourceTypeId"} render={({field}) => (
                                <select className={"select"} value={field.value} name={field.name} onChange={field.onChange}>
                                    <option value={undefined}>{t('select')}</option>
                                    {resourceTypes?.map((resourceType) => (
                                        <option key={resourceType.id}
                                                value={resourceType.id}>{resourceType.title}</option>
                                    ))}
                                </select>
                            )}/>
                        </div>

                        <Controller control={control} name={"agreementIds"}
                                    render={({field}) => (
                                        <Dropdown position={"Under Left"} toggle={
                                            <div className={"w-100"}>
                                                <InputLabel>
                                                    {t('agreements')}
                                                </InputLabel>
                                                <div className={"select"}>
                                                    {watch('agreementIds')?.length > 0 ? (
                                                        agreements?.filter(x => watch('agreementIds')?.includes(x.id)).map(x => x.nickname).join(', ')
                                                    ) : (
                                                        t('all')
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        >
                                            <>
                                                <DropdownItem onClick={() => field.onChange([])} key={"all"}>
                                                    {t('all')}
                                                </DropdownItem>
                                                <DropdownItem onClick={() => field.onChange(agreements?.map(x => x.id))} key={"all"}>
                                                    {t('selectAll')}
                                                </DropdownItem>
                                                {agreements?.sort((a, b) => a.nickname.localeCompare(b.nickname)).map(x => (
                                                    <DropdownItem
                                                        onClick={() => field.onChange(field.value?.includes(x.id) ? [...field.value.filter(v => v !== x.id)] : [...field.value, x.id])}
                                                        key={x.id}>
                                                        <FlexRow justify={"space-between"}>
                                                            <span>
                                                                {x.nickname}
                                                            </span>
                                                            {field.value?.includes(x.id) && (
                                                                <CheckmarkCircle/>
                                                            )}
                                                        </FlexRow>
                                                    </DropdownItem>
                                                ))}
                                            </>
                                        </Dropdown>
                                    )}/>

                        <div className={"w-100"}>
                            <InputLabel>
                                {t('link')}
                            </InputLabel>
                            <ControlledInput placeholder={t('link')} name={"link"} control={control} />
                        </div>

                        <div className={"w-100"}>
                            <InputLabel>
                                {t('preview')}
                            </InputLabel>
                            <FlexRow justify={"space-between"}>
                                <div>
                                    {watch('preview') && (
                                        <img src={watch('preview')?.url} alt={t('preview')} width={100}
                                             height={100}/>
                                    )}
                                </div>
                                <FileUploadButton multiple callback={file => setValue('preview', file)}/>
                            </FlexRow>
                        </div>

                        <InputLabel>
                            {t('files')}
                        </InputLabel>                             
                        {fields.map((x, index) => (
                            <React.Fragment key={x.id}>
                                <div className={"w-100"}>
                                    <InputLabel>
                                        {t('fileName')}
                                    </InputLabel>
                                    <FlexRow justify={"space-between"}>
                                        <ControlledInput control={control} name={`resourceFiles.${index}.name`} />
                                        
                                        {!!watch(`resourceFiles.${index}.file`) && (
                                            <>
                                                {watch(`resourceFiles.${index}.file`).fileName}
                                                <CloseIcon onClick={() => remove(index)} fill={"red"} />
                                            </>
                                        )}

                                        {!watch(`resourceFiles.${index}.file`) && (
                                            <FileUploadButtonV2 
                                                accept={[".csv", ".mov", ".jpg", ".jpeg", ".png", ".svg", ".xlsx", ".pdf", ".doc", ".docx", ".pptx", ".mp3", ".mp4", ".zip"]} 
                                                multiple 
                                                callback={file => setValue(`resourceFiles.${index}.file`, file)}
                                            />
                                        )}
                                    </FlexRow>
                                </div>
                                
                                <Separator />
                            </React.Fragment>
                        ))}
                        
                        <Button text={t('addFile')} onClick={() => append({ name: '', file: undefined })} outline />


                        <FlexRow justify={"end"}>
                            <Button outline state={isSavingResource ? 'Loading' : 'None'} type={"submit"}
                                    text={t('save')}/>
                        </FlexRow>
                    </Space>
                </form>
            </Modal>

            <Modal title={t('deleteResourceType')} visible={deleteModalVisible} onCancel={closeModal}>
                <form onSubmit={handleSubmit(confirmDeleteResource)}>
                    <Space>
                        <div className={"w-100"}>
                            {t('deleteResourceConfirmation')}
                        </div>

                        <FlexRow justify={"end"}>
                            <Button color={"danger"} disabled={isDeletingResource} type={"submit"}
                                    state={isDeletingResource ? 'Loading' : 'None'} text={t('delete')}/>
                        </FlexRow>
                    </Space>
                </form>
            </Modal>
            
            <ResourceBulkUploadModal onClose={() => setBulModalVisible(false)} visible={bulkModalVisible} />
        </Box>
    )

}

export default ResourcesPage
